import { IconProps } from '@chakra-ui/react'
import { t } from 'i18next'
import {
  Ban,
  FolderInput,
  Link,
  MessageSquareDot,
  MessageSquareText,
  Pencil,
  PhoneCall,
  Printer,
  RotateCw,
  Trash2,
  X,
} from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  CopyLinkToClipboardFabIcon,
  CopyLinkToClipboardFabIconProps,
} from '~/components/compositional/QuickActionIcons/CopyLinkToClipboardFabIcon'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import {
  QuickActionButton,
  QuickActionButtonSizes,
} from '~/components/shared/QuickActionButton'

type MarkAsUnreadProps = {
  action: 'mark_read' | 'mark_unread'
  onClick: () => void
  size?: QuickActionButtonSizes
}

interface QuickActionProps {
  size?: QuickActionButtonSizes
  iconProps?: IconProps
  onClick?: () => void
  tooltipText?: string
  tooltipDisabled?: boolean
}

export const Edit = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={Pencil}
      aria-label={t('voicemail.tooltip.editContact')}
      tooltipText={t('voicemail.tooltip.editContact')}
      onClick={onClick}
    />
  )
}

export const CopyLinkToClipboard = (
  props: Omit<CopyLinkToClipboardFabIconProps, 'icon'>,
) => <CopyLinkToClipboardFabIcon {...props} icon={Link} />

export const NewCall = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={PhoneCall}
      aria-label={t('voicemail.tooltip.newCall')}
      tooltipText={t('voicemail.tooltip.newCall')}
      onClick={onClick}
    />
  )
}

export const Delete = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={Trash2}
      aria-label={t('voicemail.tooltip.delete')}
      tooltipText={t('voicemail.tooltip.delete')}
      onClick={onClick}
    />
  )
}

export const ResendFax = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={RotateCw}
      aria-label={t('voicemail.tooltip.resendFax')}
      tooltipText={t('voicemail.tooltip.resendFax')}
      onClick={onClick}
    />
  )
}

export const MoveFax = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={FolderInput}
      aria-label={t('voicemail.tooltip.moveFax')}
      tooltipText={t('voicemail.tooltip.moveFax')}
      onClick={onClick}
    />
  )
}

export const NewFax = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={Printer}
      aria-label={t('voicemail.tooltip.newFax')}
      tooltipText={t('voicemail.tooltip.newFax')}
      onClick={onClick}
    />
  )
}

export const NewText = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={MessageSquareText}
      aria-label={t('voicemail.tooltip.newText')}
      tooltipText={t('voicemail.tooltip.newText')}
      onClick={onClick}
    />
  )
}

export const MarkAsUnread = ({ action, onClick, size }: MarkAsUnreadProps) => {
  const { t } = useTranslation()
  const text =
    action === 'mark_read'
      ? t('voicemail.tooltip.markAsRead')
      : t('voicemail.tooltip.markAsUnread')
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={MessageSquareDot}
      aria-label={text}
      tooltipText={text}
      onClick={onClick}
    />
  )
}

export const BlockNumber = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={Ban}
      aria-label={t('voicemail.tooltip.blockNumber')}
      tooltipText={t('voicemail.tooltip.blockNumber')}
      onClick={onClick}
    />
  )
}

export const UnblockNumber = ({
  size,
  variant,
  onClick,
  ariaLabel,
}: QuickActionProps & {
  variant?: QuickActionButtonVariants
  ariaLabel?: string
}) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={variant ?? QuickActionButtonVariants.Destructive_200}
      size={size}
      actionIcon={Ban}
      ariaLabel={ariaLabel ?? t('voicemail.tooltip.unblockNumber')}
      tooltipText={t('voicemail.tooltip.unblockNumber')}
      onClick={onClick}
    />
  )
}

export const Close = ({
  size,
  iconProps,
  onClick,
  tooltipDisabled = false,
  tooltipText = t('voicemail.tooltip.close'),
}: QuickActionProps) => (
  <QuickActionButton
    variant={QuickActionButtonVariants.Default_100}
    size={size}
    iconProps={iconProps}
    actionIcon={X}
    aria-label={tooltipText}
    tooltipText={tooltipText}
    onClick={onClick}
    tooltipDisabled={tooltipDisabled}
  />
)
